import type { ReactNode } from 'react';

import type { Session } from '@core/session/types';
import { AttributesPage } from './AttributesPage';
import { TodayPage } from './today/TodayPage';
import { AvailabilityPage } from './AvailabilityPage';
import { CataloguePage } from './catalogue/CataloguePage';
import { CalendarPage } from './CalendarPage';
import { BookingsPage } from './BookingsPage';
import { BranchesPage } from './BranchesPage';
import { CategoriesPage } from './CategoriesPage';
import { CompanyProfilePage } from './CompanyProfilePage';
import { SettingsPage } from './SettingsPage';
import { CustomersPage } from './CustomersPage';
import { DocumentNumberingPage } from './DocumentNumberingPage';
import { FinancialYearsPage } from './FinancialYearsPage';
import { GoodsReceiptsPage } from './GoodsReceiptsPage';
import { PurchaseBillsPage } from './purchases/PurchaseBillsPage';
import { PurchasesPage } from './purchases/PurchasesPage';
import { OutstandingPage } from './outstanding/OutstandingPage';
import { ReportsPage } from './reports/ReportsPage';
import { VendorPaymentsPage } from './purchases/VendorPaymentsPage';
import { SalesPage } from './sales/SalesPage';
import { InvoicesPage } from './InvoicesPage';
import { ItemsPage } from './ItemsPage';
import { RateCardsPage } from './RateCardsPage';
import { ReturnsPage } from './ReturnsPage';
import { DepositsPage } from './DepositsPage';
import { ApprovalsPage } from './ApprovalsPage';
import { DisputesPage } from './DisputesPage';
import { GarmentJobsPage } from './GarmentJobsPage';
import { RolesPage } from './RolesPage';
import { UnitsPage } from './UnitsPage';
import { UsersPage } from './UsersPage';
import { VendorsPage } from './VendorsPage';
import { EmployeesPage } from './EmployeesPage';
import { PartiesPage } from './PartiesPage';
import { CashPage } from './cash/CashPage';
import { ExpensesPage } from './cash/ExpensesPage';
import type { BuiltPageKey } from './builtKeys';

/**
 * Menu key -> screen.
 *
 * Typed as a total `Record<BuiltPageKey, …>`, so the compiler is what keeps the registry and
 * `builtKeys.ts` in step: adding a key without a screen fails the build, and shipping a screen
 * nothing routes to is caught by the menu test. Between them there is no state where the sidebar
 * and the router disagree about what exists.
 *
 * Navigation is by key rather than by URL because the shell has no router yet — `AppShell`
 * intercepts menu clicks and raises the key. The manifest already carries a `path` per item, so
 * introducing real routing later is a change to two files rather than to every screen.
 */
/**
 * What a screen is handed besides the session.
 *
 * <p>
 * Two things, and both exist for the Today screen. Its cards lead to a *saved view* of the register
 * — landing on the unfiltered list would leave the clicking still to be done, which is the whole
 * complaint that screen answers — and they have to be able to open another screen at all, which
 * until now only the sidebar could do.
 * </p>
 *
 * <p>
 * <strong>The view is not in the URL, and that is the existing rule rather than a shortcut.</strong>
 * Saved views are already parent state; putting one in the address needs route parameters, the same
 * thing the item-detail page has been waiting for. A refresh therefore lands on the register's
 * default view, exactly as a refresh on any sub-screen lands on its parent list.
 * </p>
 *
 * <p>
 * A screen that wants neither is unchanged: a function of one parameter satisfies a type of two, so
 * the other twenty-odd entries below cost nothing.
 * </p>
 */
export interface ScreenContext {
  /**
   * The saved view to open on, when whoever navigated had one in mind.
   *
   * `| undefined` spelled out because the project runs `exactOptionalPropertyTypes`: the caller
   * passes the field on every navigation and it is genuinely undefined on most of them.
   */
  view?: string | undefined;
  /**
   * A code the global find resolved, for the screen to open with in its own search box.
   *
   * <p>
   * There is no router, so a find cannot navigate to one record's address. It navigates to the
   * screen that lists that kind of record, with the exact code already searched — which on a code
   * scanned off a tag is one row. That is the honest version of "one hit navigates" in a shell
   * with no route parameters, and it is the same limitation the item-detail page has been waiting
   * on rather than a new one.
   * </p>
   */
  find?: string | undefined;
  /**
   * The one record the find settled on, for a screen that can open a record as internal state —
   * the bookings screen opens a scanned booking slip's booking rather than listing it.
   */
  record?: number | undefined;
  /** Opens another screen, optionally on one of its saved views or carrying a code to find. */
  open: (key: string, view?: string, find?: string, record?: number) => void;
}

export const pages: Record<BuiltPageKey, (session: Session, ctx: ScreenContext) => ReactNode> = {
  today: (session, ctx) => <TodayPage session={session} onOpen={ctx.open} />,
  branch: (session) => <BranchesPage session={session} />,
  user: (session) => <UsersPage session={session} />,
  role: (session) => <RolesPage session={session} />,
  fy: (session) => <FinancialYearsPage session={session} />,
  numbering: (session) => <DocumentNumberingPage session={session} />,
  company: (session) => <CompanyProfilePage session={session} />,
  settings: (session) => <SettingsPage session={session} />,
  categories: (session) => <CategoriesPage session={session} />,
  items: (session) => <ItemsPage session={session} />,
  attributes: (session) => <AttributesPage session={session} />,
  units: (session, ctx) => (
    <UnitsPage key={ctx.find ?? ''} session={session} initialSearch={ctx.find} />
  ),
  receipts: (session) => <GoodsReceiptsPage session={session} />,
  availability: (session) => <AvailabilityPage session={session} />,
  showroom: (session) => <CataloguePage session={session} />,
  calendar: (session) => <CalendarPage session={session} />,
  customers: (session) => <CustomersPage session={session} />,
  vendors: (session) => <VendorsPage session={session} />,
  employees: (session) => <EmployeesPage session={session} />,
  parties: (session) => <PartiesPage session={session} />,
  returns: (session) => <ReturnsPage session={session} />,
  deposits: () => <DepositsPage />,
  approvals: (session) => <ApprovalsPage session={session} />,
  disputes: (session) => <DisputesPage session={session} />,
  cleaning: (session) => <GarmentJobsPage session={session} kind="Cleaning" />,
  alterations: (session) => <GarmentJobsPage session={session} kind="Alteration" />,
  ratecards: (session) => <RateCardsPage session={session} />,
  purchases: (session, ctx) => (
    <PurchasesPage key={ctx.record ?? ''} session={session} vendorId={ctx.record} />
  ),
  bills: (session) => <PurchaseBillsPage session={session} />,
  vendorpayments: (session, ctx) => (
    <VendorPaymentsPage
      key={`${ctx.record ?? ''}:${ctx.view ?? ''}`}
      session={session}
      payVendorId={ctx.record}
      startNew={ctx.view === 'new'}
    />
  ),
  outstanding: (session, ctx) => <OutstandingPage session={session} onOpen={ctx.open} />,
  reports: (session, ctx) => <ReportsPage session={session} onOpen={ctx.open} />,
  pos: (session, ctx) => (
    <SalesPage
      key={`${ctx.record ?? ''}:${ctx.view ?? ''}`}
      session={session}
      openSaleId={ctx.record}
      startNew={ctx.view === 'new'}
    />
  ),
  invoices: (session) => <InvoicesPage session={session} />,
  // Money out on the cash board opens these straight on their new-entry page (`view: 'new'`).
  // `record` names the head for "Again, like last time" (view `new`) or the recurring expense (`due`).
  expenses: (session, ctx) => (
    <ExpensesPage
      key={`${ctx.view ?? ''}:${ctx.record ?? ''}`}
      session={session}
      startNew={ctx.view === 'new'}
      likeHeadId={ctx.view === 'new' ? ctx.record : undefined}
      dueId={ctx.view === 'due' ? ctx.record : undefined}
      onOpen={ctx.open}
    />
  ),
  cash: (session, ctx) => <CashPage session={session} onOpen={ctx.open} />,
  /*
    Remounted when the view changes, which is what makes a Today card land on the right tab.

    `BookingsPage` seeds its view in a `useState` initialiser, so a changed prop alone would not
    move it — and an effect that overwrote the view on every render would fight the clerk's own tab
    clicks. A `key` discards the screen's local state, which for a fresh navigation is correct.
  */
  bookings: (session, ctx) => (
    <BookingsPage
      key={`${ctx.view ?? 'all'}:${ctx.find ?? ''}:${ctx.record ?? ''}`}
      session={session}
      initialView={ctx.view}
      initialFind={ctx.find}
      initialBookingId={ctx.record}
    />
  ),
};
